/**
 * PostgresForm — PostgreSQL warehouse connection form (RHF-based, add-only).
 *
 * Collects host/port/database (prefilled read-only from RudderStack when present),
 * service username, password, optional namespace/schema, and SSL mode. Keeps the
 * selected mode in the structured config so Lookout and RudderStack use the same TLS settings.
 *
 * The host check is a factory rather than a module const because `allowPrivate` is a
 * runtime value (`WAREHOUSE_ALLOW_PRIVATE_HOSTS`, read from the WorkspaceContext flag
 * bag). It mirrors the server's `createPostgresConnectionConfigSchema(allowPrivate = false)`
 * — same default, so an unthreaded caller stays strict. This only ever widens the form to
 * what the server already accepts; the server re-validates every save regardless.
 */
import { zodResolver } from '@hookform/resolvers/zod';
import {
  DEFAULT_POSTGRES_PORT,
  isSafeSqlIdentifier,
  isValidPostgresHost,
  type PostgresPrefillConfig,
} from '@server/postgres';
import type { PostgresConnectionConfig, PostgresFormSslMode } from '@server/types';
import * as React from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { Form, FormControl, FormField } from '@/_shared/components/ui/form';
import { Input } from '@/_shared/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/_shared/components/ui/select';
import { useWorkspaceOrNull } from '@/_shared/contexts/WorkspaceContext';

import {
  type FormDraft,
  useDraftDefaults,
  useResetOnChange,
  useWarehouseFormHandle,
  type WarehouseFormHandle,
} from '../shared/formHandle';
import { FormSection } from '../shared/FormSection';
import { HiddenFieldErrors } from '../shared/HiddenFieldErrors';

interface PostgresFormProps {
  /** Values typed before the add flow remounted the form for a new account (see `useDraftDefaults`). */
  draft?: FormDraft;
  /** The add flow names the account above the form (ACT2-1031), so its identity fields are not repeated here. */
  hideIdentity?: boolean;
  initialConfig?: PostgresPrefillConfig;
  onChange?: () => void;
}

interface PostgresFormValues {
  user: string;
  password: string;
  schema: string;
  host: string;
  port: string;
  database: string;
  sslmode: PostgresFormSslMode;
}

function createPostgresFormSchema(allowPrivate = false) {
  return z.object({
    user: z.string().trim().min(1, 'Service username is required'),
    password: z.string().refine((v) => v.length === 0 || v.trim().length > 0, 'Password cannot be only whitespace'),
    schema: z
      .string()
      .refine(
        (v) => !v.trim() || isSafeSqlIdentifier(v.trim()),
        'Invalid namespace: use letters, numbers, and underscores; must start with a letter or underscore',
      ),
    host: z
      .string()
      .trim()
      .min(1, 'Host is required')
      // Wrapped, not passed by reference: zod hands the check only the value, so the options
      // argument would be undefined and `allowPrivate` would silently fall back to false.
      .refine((v) => isValidPostgresHost(v, { allowPrivate }), 'Invalid host'),
    port: z
      .string()
      .trim()
      .refine((v) => {
        const n = Number(v);
        return Number.isInteger(n) && n >= 1 && n <= 65535;
      }, 'Port must be a number between 1 and 65535'),
    database: z.string().trim().min(1, 'Database is required'),
    sslmode: z.enum(['require', 'disable']),
  });
}

export const PostgresForm = React.forwardRef<WarehouseFormHandle, PostgresFormProps>(function PostgresForm(
  { draft, initialConfig, onChange, hideIdentity = false },
  ref,
): React.JSX.Element {
  const allowPrivate = useWorkspaceOrNull()?.allowsPrivateWarehouseHosts ?? false;
  const schema = React.useMemo(() => createPostgresFormSchema(allowPrivate), [allowPrivate]);
  const { base, defaults } = useDraftDefaults<PostgresFormValues>(
    {
      user: initialConfig?.user ?? '',
      password: '',
      schema: initialConfig?.schema ?? '',
      host: initialConfig?.host ?? '',
      port: String(initialConfig?.port ?? DEFAULT_POSTGRES_PORT),
      database: initialConfig?.database ?? '',
      sslmode: 'require',
    },
    draft,
  );
  const form = useForm<PostgresFormValues>({
    resolver: zodResolver(schema),
    mode: 'onTouched',
    defaultValues: defaults,
  });

  const hostReadOnly = Boolean(initialConfig?.host?.trim());
  const portReadOnly = initialConfig?.port != null;
  const databaseReadOnly = Boolean(initialConfig?.database?.trim());

  const buildConfig = React.useCallback((values: PostgresFormValues): PostgresConnectionConfig => {
    const config: PostgresConnectionConfig = {
      host: values.host.trim(),
      port: Number(values.port) || DEFAULT_POSTGRES_PORT,
      database: values.database.trim(),
      user: values.user.trim(),
      password: values.password.trim(),
      sslmode: values.sslmode,
    };
    if (values.schema.trim()) config.schema = values.schema.trim();
    return config;
  }, []);

  useWarehouseFormHandle(ref, form, buildConfig, base);
  useResetOnChange(form, onChange);

  return (
    <Form {...form}>
      <form onSubmit={(e) => e.preventDefault()} className="grid gap-5" noValidate>
        {hideIdentity && (
          <HiddenFieldErrors
            control={form.control}
            fields={[
              { name: 'host', label: 'Host' },
              { name: 'database', label: 'Database' },
            ]}
          />
        )}
        <FormField
          control={form.control}
          name="user"
          render={({ field }) => (
            <FormSection label="Service Username" description="The user Lookout signs in as.">
              <Input {...field} placeholder="lookout_service_user" />
            </FormSection>
          )}
        />

        <FormField
          control={form.control}
          name="password"
          render={({ field }) => (
            <FormSection label="Password" description="Stored encrypted.">
              <Input {...field} type="password" autoComplete="off" placeholder="Enter the service user password" />
            </FormSection>
          )}
        />

        {!hideIdentity && (
          <FormField
            control={form.control}
            name="host"
            render={({ field }) => (
              <FormSection label="Host">
                <Input
                  {...field}
                  readOnly={hostReadOnly}
                  aria-readonly={hostReadOnly || undefined}
                  placeholder="db.example.com"
                />
              </FormSection>
            )}
          />
        )}

        <FormField
          control={form.control}
          name="port"
          render={({ field }) => (
            <FormSection label="Port">
              <Input {...field} readOnly={portReadOnly} aria-readonly={portReadOnly || undefined} placeholder="5432" />
            </FormSection>
          )}
        />

        {!hideIdentity && (
          <FormField
            control={form.control}
            name="database"
            render={({ field }) => (
              <FormSection label="Database">
                <Input
                  {...field}
                  readOnly={databaseReadOnly}
                  aria-readonly={databaseReadOnly || undefined}
                  placeholder="analytics"
                />
              </FormSection>
            )}
          />
        )}

        <FormField
          control={form.control}
          name="schema"
          render={({ field }) => (
            <FormSection label="Namespace" optional description="Default schema for queries. Empty means public.">
              <Input {...field} placeholder="public" />
            </FormSection>
          )}
        />

        <FormField
          control={form.control}
          name="sslmode"
          render={({ field }) => (
            <FormSection
              label="SSL Mode"
              description={
                <>
                  <span className="block">require encrypts the connection; disable does not.</span>
                  {initialConfig?.rsSslMode && initialConfig.rsSslMode.toLowerCase() !== 'require' && (
                    <span className="block">The RudderStack destination uses {initialConfig.rsSslMode}.</span>
                  )}
                </>
              }
              omitFormControl
            >
              <Select value={field.value} onValueChange={field.onChange}>
                <FormControl>
                  <SelectTrigger ref={field.ref} onBlur={field.onBlur} className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value="require">require</SelectItem>
                  <SelectItem value="disable">disable</SelectItem>
                </SelectContent>
              </Select>
            </FormSection>
          )}
        />
      </form>
    </Form>
  );
});
